import { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Mail,
  Globe,
  Smartphone,
  Download,
  RefreshCw,
  Lock,
  LogOut,
  Search,
  Send,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { BACKEND_URL } from "../lib/backend";

export const TOKEN_KEY = "nephrix_admin_token";

const userTypeLabels = {
  dialysis_patient: "Pacient dializă",
  transplant_patient: "Pacient transplant",
  ckd: "BCR (fără dializă)",
  caregiver: "Îngrijitor",
  healthcare_professional: "Cadru medical",
};

const deviceLabels = { iphone: "iPhone", android: "Android", both: "Ambele" };
const langLabels = { ro: "Română", en: "Engleză", fr: "Franceză" };
const statusLabels = { waiting_list: "În așteptare", invited: "Invitat" };

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem(TOKEN_KEY, data.token);
        onLogin();
      } else if (res.status === 429) {
        setError("Prea multe încercări. Așteaptă un minut și reîncearcă.");
      } else {
        setError("Parolă incorectă.");
      }
    } catch (_e) {
      setError("Eroare de conexiune la server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 w-full max-w-sm"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col items-center mb-6">
          <img src="/nephrix-logo.png" alt="Nephrix" className="h-12 w-12 object-contain mb-3" />
          <div className="flex items-center gap-2 text-slate-900 font-bold text-xl">
            <Lock className="w-5 h-5 text-blue-600" />
            Zonă Admin
          </div>
          <p className="text-sm text-slate-500 mt-1">Introdu parola de administrator</p>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Parolă"
          autoFocus
          className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 mb-3"
          data-testid="admin-password-input"
        />
        {error && (
          <p className="text-sm text-red-500 mb-3" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !password}
          className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {loading ? "Se verifică..." : "Autentificare"}
        </button>
        <Link
          to="/"
          className="block text-center text-sm text-slate-500 hover:text-blue-600 mt-4"
          data-testid="admin-back-home"
        >
          ← Înapoi la site
        </Link>
      </motion.form>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, value, label, testId }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

const FilterSelect = ({ value, onChange, options, allLabel, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
    data-testid={testId}
  >
    <option value="">{allLabel}</option>
    {options.map(([val, label]) => (
      <option key={val} value={val}>
        {label}
      </option>
    ))}
  </select>
);

const BetaAdmin = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fDevice, setFDevice] = useState("");
  const [fLang, setFLang] = useState("");
  const [fType, setFType] = useState("");
  const [invitingId, setInvitingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setEntries([]);
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/beta-signup/admin`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (res.ok) {
        setEntries(await res.json());
      } else {
        setError("Nu s-au putut încărca înscrierile.");
      }
    } catch (_e) {
      setError("Eroare de conexiune la server.");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (authed) fetchEntries();
  }, [authed, fetchEntries]);

  const sendInvite = async (entry) => {
    const isResend = entry.status === "invited";
    const msg = isResend
      ? `Retrimiți invitația de beta către ${entry.first_name} (${entry.email})?`
      : `Trimiți invitația de beta către ${entry.first_name} (${entry.email})?`;
    if (!window.confirm(msg)) return;
    setInvitingId(entry.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/beta-signup/${entry.id}/invite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setEntries((prev) =>
          prev.map((e) => (e.id === entry.id ? { ...e, status: "invited", invited_at: data.invited_at } : e))
        );
        showToast("success", `Invitația a fost trimisă către ${entry.email}`);
      } else {
        showToast("error", "Trimiterea invitației a eșuat. Reîncearcă.");
      }
    } catch (_e) {
      showToast("error", "Eroare de conexiune la server.");
    } finally {
      setInvitingId(null);
    }
  };

  const countries = useMemo(
    () => [...new Set(entries.map((e) => e.country))].sort(),
    [entries]
  );

  const filtered = useMemo(
    () =>
      entries.filter(
        (e) =>
          (!fCountry || e.country === fCountry) &&
          (!fDevice || e.device === fDevice) &&
          (!fLang || e.preferred_language === fLang) &&
          (!fType || e.user_type === fType) &&
          (!search ||
            e.email.toLowerCase().includes(search.toLowerCase()) ||
            e.first_name.toLowerCase().includes(search.toLowerCase()))
      ),
    [entries, fCountry, fDevice, fLang, fType, search]
  );

  const stats = useMemo(
    () => ({
      total: entries.length,
      countries: new Set(entries.map((e) => e.country)).size,
      iphone: entries.filter((e) => e.device === "iphone" || e.device === "both").length,
      android: entries.filter((e) => e.device === "android" || e.device === "both").length,
    }),
    [entries]
  );

  const exportCSV = () => {
    const headers = ["Prenume", "Email", "Țară", "Tip utilizator", "Dispozitiv", "Limbă", "Status", "Consimțământ GDPR", "Data înscrierii"];
    const rows = filtered.map((e) => [
      e.first_name,
      e.email,
      e.country,
      userTypeLabels[e.user_type] || e.user_type,
      deviceLabels[e.device] || e.device,
      langLabels[e.preferred_language] || e.preferred_language,
      statusLabels[e.status] || e.status,
      e.gdpr_consent ? "Da" : "Nu",
      formatDate(e.created_at),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = `nephrix-beta-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
          }`}
          data-testid="admin-toast"
        >
          {toast.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.text}
        </div>
      )}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/nephrix-logo.png" alt="Nephrix" className="h-10 w-10 object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium"
              data-testid="back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi la site
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors text-sm font-medium"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Delogare
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1
                className="text-3xl font-bold text-slate-900 mb-2"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="beta-admin-title"
              >
                Înscrieri Beta Test
              </h1>
              <p className="text-slate-600">Lista persoanelor înscrise în programul beta Nephrix</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchEntries}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Reîncarcă
              </button>
              <button
                onClick={exportCSV}
                disabled={filtered.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} color="bg-blue-100 text-blue-600" value={stats.total} label="Total înscriși" testId="stat-total" />
            <StatCard icon={Globe} color="bg-teal-100 text-teal-600" value={stats.countries} label="Țări" testId="stat-countries" />
            <StatCard icon={Smartphone} color="bg-slate-100 text-slate-600" value={stats.iphone} label="iPhone" testId="stat-iphone" />
            <StatCard icon={Smartphone} color="bg-green-100 text-green-600" value={stats.android} label="Android" testId="stat-android" />
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută după nume sau email..."
                className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                data-testid="search-input"
              />
            </div>
            <FilterSelect value={fCountry} onChange={setFCountry} options={countries.map((c) => [c, c])} allLabel="Toate țările" testId="filter-country" />
            <FilterSelect value={fType} onChange={setFType} options={Object.entries(userTypeLabels)} allLabel="Toate tipurile" testId="filter-type" />
            <FilterSelect value={fDevice} onChange={setFDevice} options={Object.entries(deviceLabels)} allLabel="Toate dispozitivele" testId="filter-device" />
            <FilterSelect value={fLang} onChange={setFLang} options={Object.entries(langLabels)} allLabel="Toate limbile" testId="filter-language" />
            {(fCountry || fDevice || fLang || fType || search) && (
              <button
                onClick={() => {
                  setFCountry("");
                  setFDevice("");
                  setFLang("");
                  setFType("");
                  setSearch("");
                }}
                className="text-sm text-blue-600 hover:underline"
                data-testid="clear-filters-btn"
              >
                Resetează filtrele
              </button>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Se încarcă înscrierile...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <p className="text-red-500" data-testid="admin-error">{error}</p>
                <button onClick={fetchEntries} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Reîncearcă
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500" data-testid="empty-state">
                  {entries.length === 0 ? "Nicio înscriere încă" : "Niciun rezultat pentru filtrele alese"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="beta-signups-table">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      {["#", "Prenume", "Email", "Țară", "Tip utilizator", "Dispozitiv", "Limbă", "Status", "Data", "Acțiuni"].map((h) => (
                        <th key={h} className="text-left px-5 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((entry, index) => (
                      <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-4 text-sm text-slate-400">{index + 1}</td>
                        <td className="px-5 py-4 text-sm font-medium text-slate-900">{entry.first_name}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700">{entry.email}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-700">{entry.country}</td>
                        <td className="px-5 py-4">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                            {userTypeLabels[entry.user_type] || entry.user_type}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-700">{deviceLabels[entry.device] || entry.device}</td>
                        <td className="px-5 py-4 text-sm text-slate-700">{langLabels[entry.preferred_language] || entry.preferred_language}</td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              entry.status === "invited" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                            }`}
                            data-testid={`status-badge-${index}`}
                          >
                            {statusLabels[entry.status] || entry.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(entry.created_at)}</td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => sendInvite(entry)}
                            disabled={invitingId === entry.id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap ${
                              entry.status === "invited"
                                ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                : "bg-teal-600 text-white hover:bg-teal-700"
                            }`}
                            data-testid={`invite-btn-${index}`}
                          >
                            {invitingId === entry.id ? (
                              <span className="animate-spin w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full" />
                            ) : (
                              <Send className="w-3.5 h-3.5" />
                            )}
                            {entry.status === "invited" ? "Retrimite" : "Invită"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && !error && entries.length > 0 && (
            <p className="text-sm text-slate-500 mt-4" data-testid="results-count">
              Afișate {filtered.length} din {entries.length} înscrieri
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default BetaAdmin;
